import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Upload, Trash2, X, Play, Pause, Mic, Clock,
  ChevronDown, ChevronUp, Pencil, CalendarClock
} from "lucide-react";
import type { PodcastEpisode } from "@shared/schema";

function AudioPlayer({ src, title }: { src: string; title: string }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  function togglePlay() {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  }

  function formatTime(seconds: number) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  }

  return (
    <div className="flex items-center gap-4 bg-background/50 border border-white/10 rounded-xl p-4">
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime || 0)}
        onLoadedMetadata={() => setDuration(audioRef.current?.duration || 0)}
        onEnded={() => setIsPlaying(false)}
      />
      <button
        onClick={togglePlay}
        aria-label={isPlaying ? `Pause ${title}` : `Play ${title}`}
        className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 hover:opacity-90 transition-opacity"
        data-testid={`button-play-${title}`}
      >
        {isPlaying ? <Pause size={20} aria-hidden="true" /> : <Play size={20} className="ml-0.5" aria-hidden="true" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className="relative h-2 bg-white/10 rounded-full overflow-hidden cursor-pointer"
          role="slider"
          tabIndex={0}
          aria-label="Audio playback progress"
          aria-valuenow={Math.round(currentTime)}
          aria-valuemin={0}
          aria-valuemax={Math.round(duration)}
          aria-valuetext={`${formatTime(currentTime)} of ${formatTime(duration)}`}
          onClick={(e) => {
            if (!audioRef.current || !duration) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const pct = (e.clientX - rect.left) / rect.width;
            audioRef.current.currentTime = pct * duration;
          }}
          onKeyDown={(e) => {
            if (!audioRef.current || !duration) return;
            const step = 5;
            if (e.key === "ArrowRight") { e.preventDefault(); audioRef.current.currentTime = Math.min(duration, currentTime + step); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); audioRef.current.currentTime = Math.max(0, currentTime - step); }
          }}
        >
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all"
            style={{ width: duration ? `${(currentTime / duration) * 100}%` : "0%" }}
          />
        </div>
        <div className="flex justify-between text-xs text-muted-foreground mt-1">
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </div>
      </div>
    </div>
  );
}

export default function Podcast() {
  const { user } = useAuth();
  const isOwner = ["matt@mattrebeiro.com", "matt@learnpdi.com"].includes(user?.email || "");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [showUpload, setShowUpload] = useState(false);
  const [expandedEpisode, setExpandedEpisode] = useState<number | null>(null);
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    coverImage: "",
    duration: "",
    episodeNumber: "",
    season: "",
    published: true,
    scheduledAt: "",
  });

  const { data: episodes, isLoading } = useQuery<PodcastEpisode[]>({
    queryKey: ["/api/podcast", isOwner ? "all" : "published"],
    queryFn: async () => {
      const url = isOwner ? "/api/podcast" : "/api/podcast?published=true";
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch episodes");
      return res.json();
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!audioFile) throw new Error("Please select an audio file");
      if (!form.title.trim()) throw new Error("Title is required");

      const formData = new FormData();
      formData.append("audio", audioFile);
      formData.append("title", form.title);
      formData.append("description", form.description);
      formData.append("coverImage", form.coverImage);
      formData.append("duration", form.duration);
      formData.append("episodeNumber", form.episodeNumber);
      formData.append("season", form.season);
      formData.append("published", form.published.toString());
      if (form.scheduledAt) {
        formData.append("scheduledAt", form.scheduledAt);
      }

      const res = await fetch("/api/podcast", {
        method: "POST",
        body: formData,
        credentials: "include",
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Upload failed");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/podcast"] });
      setShowUpload(false);
      setAudioFile(null);
      setForm({ title: "", description: "", coverImage: "", duration: "", episodeNumber: "", season: "", published: true, scheduledAt: "" });
      toast({ title: "Episode uploaded successfully" });
    },
    onError: (err: Error) => {
      toast({ title: "Upload failed", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/podcast/${id}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/podcast"] });
      toast({ title: "Episode deleted" });
    },
  });

  const togglePublish = useMutation({
    mutationFn: async ({ id, published }: { id: number; published: boolean }) => {
      const body: any = { published };
      if (published) body.scheduledAt = null;
      const res = await fetch(`/api/podcast/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to update");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/podcast"] });
    },
  });

  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-4xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Podcast</span>
              </div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6">The Startup Bootstrapper</h1>
                  <p className="text-xl text-muted-foreground max-w-2xl">
                    Real conversations about building, scaling, and leading startups.
                  </p>
                </div>
                {isOwner && (
                  <Button onClick={() => setShowUpload(true)} className="shrink-0" data-testid="button-upload-episode">
                    <Upload size={16} className="mr-2" /> Upload Episode
                  </Button>
                )}
              </div>
            </div>

            {showUpload && isOwner && (
              <Card className="border-white/10 p-6 mb-12">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-lg font-semibold">Upload New Episode</h2>
                  <button onClick={() => setShowUpload(false)} aria-label="Close upload form" className="p-3 -m-2 rounded-md hover:bg-white/5 transition-colors"><X size={18} className="text-muted-foreground" aria-hidden="true" /></button>
                </div>
                <form className="space-y-5" onSubmit={(e) => { e.preventDefault(); uploadMutation.mutate(); }}>
                  <div
                    className="border-2 border-dashed border-white/10 rounded-xl p-8 text-center cursor-pointer hover:border-primary/40 transition-colors"
                    role="button"
                    tabIndex={0}
                    aria-label={audioFile ? `Selected file: ${audioFile.name}. Click to change.` : "Click to select an audio file"}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fileInputRef.current?.click(); } }}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".mp3,.wav,.m4a,.ogg,.aac"
                      className="hidden"
                      aria-label="Select audio file"
                      onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                      data-testid="input-audio-file"
                    />
                    {audioFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <Mic size={20} className="text-primary" aria-hidden="true" />
                        <span className="font-medium">{audioFile.name}</span>
                        <span className="text-sm text-muted-foreground">({(audioFile.size / (1024 * 1024)).toFixed(1)} MB)</span>
                      </div>
                    ) : (
                      <div>
                        <Upload size={32} className="mx-auto mb-3 text-muted-foreground" aria-hidden="true" />
                        <p className="text-muted-foreground">Click to select an audio file</p>
                        <p className="text-xs text-muted-foreground mt-1">MP3, WAV, M4A, OGG, AAC — up to 200MB</p>
                      </div>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="pod-title">Episode Title</Label>
                      <Input
                        id="pod-title"
                        value={form.title}
                        onChange={(e) => setForm({ ...form, title: e.target.value })}
                        placeholder="Episode title"
                        className="bg-background border-white/10"
                        required
                        data-testid="input-episode-title"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pod-duration">Duration</Label>
                      <Input
                        id="pod-duration"
                        value={form.duration}
                        onChange={(e) => setForm({ ...form, duration: e.target.value })}
                        placeholder="45:30"
                        className="bg-background border-white/10"
                        data-testid="input-episode-duration"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pod-number">Episode Number</Label>
                      <Input
                        id="pod-number"
                        type="number"
                        value={form.episodeNumber}
                        onChange={(e) => setForm({ ...form, episodeNumber: e.target.value })}
                        placeholder="1"
                        className="bg-background border-white/10"
                        data-testid="input-episode-number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pod-season">Season</Label>
                      <Input
                        id="pod-season"
                        type="number"
                        value={form.season}
                        onChange={(e) => setForm({ ...form, season: e.target.value })}
                        placeholder="1"
                        className="bg-background border-white/10"
                        data-testid="input-episode-season"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pod-description">Description</Label>
                    <Textarea
                      id="pod-description"
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="What's this episode about?"
                      rows={4}
                      className="bg-background border-white/10"
                      data-testid="input-episode-description"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="pod-cover">Cover Image URL (optional)</Label>
                    <Input
                      id="pod-cover"
                      value={form.coverImage}
                      onChange={(e) => setForm({ ...form, coverImage: e.target.value })}
                      placeholder="https://..."
                      className="bg-background border-white/10"
                      data-testid="input-episode-cover"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        id="pod-published"
                        checked={form.published}
                        onCheckedChange={(checked) => setForm({ ...form, published: checked, scheduledAt: checked ? "" : form.scheduledAt })}
                        data-testid="switch-episode-published"
                      />
                      <Label htmlFor="pod-published">Publish immediately</Label>
                    </div>

                    {!form.published && (
                      <div className="space-y-2 pl-1">
                        <Label htmlFor="pod-scheduled" className="flex items-center gap-2 text-sm text-muted-foreground">
                          <CalendarClock className="h-4 w-4" aria-hidden="true" />
                          Schedule publish date & time
                        </Label>
                        <Input
                          id="pod-scheduled"
                          type="datetime-local"
                          value={form.scheduledAt}
                          onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
                          min={new Date().toISOString().slice(0, 16)}
                          className="bg-background border-white/10 max-w-xs"
                          data-testid="input-episode-scheduled"
                          aria-label="Schedule publish date and time"
                        />
                        {!form.scheduledAt && (
                          <p className="text-xs text-muted-foreground">Leave empty to save as draft without scheduling.</p>
                        )}
                      </div>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={uploadMutation.isPending || !audioFile}
                    className="min-w-[160px]"
                    data-testid="button-submit-episode"
                  >
                    {uploadMutation.isPending ? "Uploading..." : "Upload Episode"}
                  </Button>
                </form>
              </Card>
            )}

            {isLoading ? (
              <div className="space-y-6" aria-busy="true" aria-label="Loading podcast episodes">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-card border border-white/5 rounded-2xl p-8 animate-pulse">
                    <div className="h-4 bg-white/5 rounded w-1/4 mb-4" />
                    <div className="h-6 bg-white/5 rounded w-3/4 mb-4" />
                    <div className="h-4 bg-white/5 rounded w-full" />
                  </div>
                ))}
              </div>
            ) : episodes && episodes.length > 0 ? (
              <div className="space-y-6" role="list" aria-label="Podcast episodes">
                {episodes.map((ep) => (
                  <Card
                    key={ep.id}
                    role="listitem"
                    className={`border-white/5 overflow-hidden transition-all ${!ep.published && isOwner ? "border-yellow-500/20" : ""}`}
                    data-testid={`card-episode-${ep.id}`}
                  >
                    <div className="p-6 md:p-8">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 mb-3 flex-wrap">
                            {ep.season && ep.episodeNumber && (
                              <Badge variant="secondary" className="bg-primary/10 text-primary text-xs">
                                S{ep.season} E{ep.episodeNumber}
                              </Badge>
                            )}
                            {ep.duration && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock size={12} aria-hidden="true" /> {ep.duration}
                              </span>
                            )}
                            {!ep.published && isOwner && (
                              ep.scheduledAt ? (
                                <Badge variant="secondary" className="bg-blue-500/10 text-blue-400 text-xs flex items-center gap-1">
                                  <CalendarClock size={10} aria-hidden="true" />
                                  Scheduled: {new Date(ep.scheduledAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} at {new Date(ep.scheduledAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                                </Badge>
                              ) : (
                                <Badge variant="secondary" className="bg-yellow-500/10 text-yellow-400 text-xs">Draft</Badge>
                              )
                            )}
                            <span className="text-xs text-muted-foreground">
                              {new Date(ep.createdAt).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
                            </span>
                          </div>

                          <h2 className="text-xl md:text-2xl font-display font-bold mb-2">
                            <button
                              type="button"
                              className="text-left hover:text-primary transition-colors w-full"
                              aria-expanded={expandedEpisode === ep.id}
                              onClick={() => setExpandedEpisode(expandedEpisode === ep.id ? null : ep.id)}
                            >
                              {ep.title}
                            </button>
                          </h2>

                          {ep.description && (
                            <p className={`text-muted-foreground leading-relaxed ${expandedEpisode === ep.id ? "" : "line-clamp-2"}`}>
                              {ep.description}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => setExpandedEpisode(expandedEpisode === ep.id ? null : ep.id)}
                            aria-label={expandedEpisode === ep.id ? `Collapse ${ep.title}` : `Expand ${ep.title}`}
                            aria-expanded={expandedEpisode === ep.id}
                            className="p-3 text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {expandedEpisode === ep.id ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
                          </button>
                          {isOwner && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => togglePublish.mutate({ id: ep.id, published: !ep.published })}
                                className="text-xs"
                                data-testid={`button-toggle-publish-${ep.id}`}
                              >
                                {ep.published ? "Unpublish" : "Publish"}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400"
                                aria-label={`Delete ${ep.title}`}
                                onClick={() => { if (confirm("Delete this episode?")) deleteMutation.mutate(ep.id); }}
                                data-testid={`button-delete-episode-${ep.id}`}
                              >
                                <Trash2 size={14} aria-hidden="true" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {expandedEpisode === ep.id && (
                        <div className="mt-6">
                          <AudioPlayer src={ep.audioUrl} title={ep.title} />
                        </div>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-card border border-white/5 rounded-2xl">
                <Mic size={40} className="mx-auto mb-4 text-muted-foreground" aria-hidden="true" />
                <h2 className="text-xl font-display font-semibold mb-3">No episodes yet</h2>
                <p className="text-muted-foreground">Check back soon for new podcast episodes.</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
